<template>
  <div>
    <!-- First Modal -->
    <b-modal
      id="complete-with-note-modal"
      dialog-class="complete-trip-modal"
      @hide="resetPayload"
      :no-close-on-backdrop="true"
    >
      <div class="complete-with-note-modal px-4">
        <div class="d-flex align-items-center border-bottom border-info pb-3">
          <img
            src="@/assets/images/icons/confirm-with-note-icon.svg"
            alt="popup vehicle"
            class="me-2"
          />
          <h4 class="m-0">{{ $t('confirmation-with-note') }}</h4>
        </div>
        <b-alert :show="error && error.length ? true : false" variant="danger" class="mt-3">
          <div v-if="Array.isArray(error)">
            <div class="d-flex flex-column my-1" v-for="(error_text, index) in error" :key="index">
              {{ error_text }}
            </div>
          </div>
          <div v-else>
            <div v-html="error || '&nbsp;'"></div>
          </div>
        </b-alert>
        <div class="mt-3">
          <div class="desc-sm mb-1">
            {{ $t('complete-with-note-question', { id }) }}
          </div>
        </div>

        <div class="mt-4">
          <validation-observer ref="observer">
            <div class="row mb-2">
              <div class="col-12">
                <base-input
                  type="number"
                  label="allowance-amount-max"
                  name="allowance-amount-max"
                  placeholder="allowance-amount-max"
                  id="service-allowance-amount-complete-note"
                  field="serviceAllowance"
                  :value="formattedAmount(serviceAllowance)"
                  :disabled="true"
                />
              </div>
              <div class="col-12">
                <base-input
                  type="number"
                  label="allowance-amount"
                  name="allowance-amount"
                  placeholder="allowance-amount"
                  :rules="{
                    min_value: 0,
                    maxValue: serviceAllowance,
                    decimalDigits: 2,
                  }"
                  id="allowance-amount-complete-note"
                  field="allowanceAmount"
                  :value="payload.allowanceAmount"
                  @change-value="setValue"
                />
              </div>
              <div class="col-12">
                <base-select
                  name="reason"
                  rules="required"
                  label="reason"
                  :is_required="true"
                  :options="reasonList"
                  field="reason"
                  :value="payload.reason"
                  @change-value="setValue"
                  :useI18n="true"
                ></base-select>
              </div>
              <div class="col-12">
                <base-textarea
                  :value="payload.note"
                  label="note"
                  placeholder="note"
                  field="note"
                  name="note"
                  @change-value="setValue"
                />
              </div>
            </div>
          </validation-observer>
        </div>
        <div class="row justify-content-end align-items-center">
          <div class="col-3 mb-3">
            <base-button variant="secondary" @click="hideModal" :disabled="loading">
              {{ $t('cancel') }}
            </base-button>
          </div>
          <div class="col-3 mb-3">
            <base-button variant="primary" @click="submit" :loading="loading">
              {{ $t('confirm') }}
            </base-button>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="new-trip-scheduled-modal"
      dialog-class="new-trip-scheduled-modal"
      :no-close-on-backdrop="true"
      :hide-footer="true"
      :hide-header="true"
      centered
    >
      <div class="new-trip-scheduled-content text-center py-4 px-3">
        <div class="success-icon-wrapper mb-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="56"
            height="56"
            fill="none"
            viewBox="0 0 56 56"
          >
            <circle cx="28" cy="28" r="28" fill="#E6F9F0" />
            <path
              d="M16 28.5L23.5 36L40 20"
              stroke="#28A745"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h5 class="success-title mb-2">{{ $t('success') }}</h5>
        <p class="success-message">{{ $t('regenerated-trip-scheduled') }}</p>
        <div class="mt-3">
          <base-button variant="primary" @click="viewAndUpdate">
            {{ $t('view-trip') }}
          </base-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TripService from '@/services/trip.service'

export default {
  emits: ['updateTrip'],
  props: ['id', 'serviceAllowance'],
  data() {
    return {
      loading: false,
      error: null,
      updatedTrip: null,
      regeneratedTripId: null,
      payload: {
        reason: null,
        note: null,
        allowanceAmount: this.serviceAllowance,
      },
      reasonList: [
        {
          value: 'The beneficiary is not reachable',
          text: 'The_Beneficiary_Is_Not_Reachable',
        },
        {
          value: 'Others',
          text: 'others',
        },
      ],
    }
  },
  methods: {
    async submit() {
      // console.log("🚀 submit clicked");

      const valid = await this.$refs.observer.validate()
      // console.log("form valid:", valid);

      if (!valid) return

      this.loading = true
      this.error = null

      const data = {
        reason: this.payload?.reason,
        id: this.id,
        allowanceAmount: Number(this.payload?.allowanceAmount),
      }

      if (this.payload?.note) data.note = this.payload.note

      // console.log("📤 sending payload", data);

      TripService.put(data, 'complete-note')
        .then((res) => {
          // console.log("✅ complete-note response", res);

          this.regeneratedTripId = res?.regeneratedTripId

          this.$bvModal.hide('complete-with-note-modal')

          this.$nextTick(() => {
            // console.log("📦 showing success modal");

            this.$bvModal.show('new-trip-scheduled-modal')

            this.autoCloseTimer = setTimeout(() => {
              // console.log("⏰ auto closing modal after 5s");

              this.$bvModal.hide('new-trip-scheduled-modal')

              // console.log("🔄 calling fetchNewTrip");

              this.fetchNewTrip()
            }, 5000)
          })
        })
        .catch((err) => {
          console.error('❌ complete-note error', err)
          this.error = err?.response?.data?.message || err?.message
        })
        .finally(() => {
          this.loading = false
        })
    },
    async fetchNewTrip() {
      console.log('📡 fetching new trip using partnerId:', this.id)

      try {
        const res = await TripService.get(`?partnerId=${this.id}&limit=1`)

        // console.log("📥 trip API response", res);

        const newTrip = res?.items?.[0]

        // console.log("🆕 new generated trip:", newTrip);

        if (newTrip) {
          this.updatedTrip = newTrip

          // console.log("📢 emitting updateTrip to parent");

          this.$emit('updateTrip', {
            trip: newTrip,
            regeneratedTripId: this.regeneratedTripId,
          })
          this.$emit('refetchData')
        } else {
          console.warn('⚠️ no new trip returned')
        }
      } catch (error) {
        console.error('❌ fetchNewTrip failed', error)
      }
    },

    openTripDetails() {
      clearTimeout(this.autoCloseTimer)
      this.$bvModal.hide('new-trip-scheduled-modal')
      this.$nextTick(() => {
        this.$bvModal.show('trip-details-modal')
      })
    },
    viewAndUpdate() {
      clearTimeout(this.autoCloseTimer)
      this.$bvModal.hide('new-trip-scheduled-modal')
      this.fetchNewTrip()
    },
    formatTime(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    setValue(val, field) {
      this['payload'][field] = val
    },
    resetPayload() {
      this.error = null
      this.payload = {
        reason: null,
        note: null,
      }
    },
    hideModal() {
      this.$bvModal.hide('complete-with-note-modal')
      this.payload.allowanceAmount = this.serviceAllowance
    },
  },
  i18n: {
    messages: {
      en: require('@/services/i18n/locales/en/reservationOperations.json'),
      ar: require('@/services/i18n/locales/ar/reservationOperations.json'),
    },
  },
}
</script>

<style lang="scss" scoped>
.complete-with-note-modal {
  padding: 3rem 0rem 1rem;

  .modal-content {
    width: 740px;
  }
}
</style>
<style lang="scss">
.complete-trip-modal {
  .modal-content {
    width: 550px;
  }
}

.new-trip-scheduled-content {
  .success-icon-wrapper {
    display: flex;
    justify-content: center;
  }

  .success-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #28a745;
  }

  .success-message {
    font-size: 0.95rem;
    color: #444;
    margin: 0;
  }

  .countdown-bar {
    width: 100%;
    height: 5px;
    background-color: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;

    .countdown-fill {
      height: 100%;
      width: 100%;
      background-color: #28a745;
      border-radius: 4px;
      animation: shrink linear forwards;
      animation-duration: inherit;
    }
  }
}

@keyframes shrink {
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
}
</style>

<style lang="scss">
.complete-trip-modal {
  .modal-content {
    width: 550px;
  }
}

.new-trip-scheduled-modal {
  .modal-content {
    width: 420px;
    border-radius: 12px;
    transform: translate(50%, 10px);
  }
}

.trip-details-modal .modal-content {
  width: 500px;
  border-radius: 12px;
}

.trip-details-content {
  .trip-details-title {
    font-size: 1rem;
    font-weight: 600;
  }

  .btn-close-custom {
    background: none;
    border: none;
    font-size: 1rem;
    cursor: pointer;
    color: #888;
    line-height: 1;

    &:hover {
      color: #333;
    }
  }

  .trip-status-badge {
    display: inline-block;
    padding: 4px 14px;
    background: #e6f9f0;
    color: #28a745;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .trip-info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .trip-info-card {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
  }

  .info-label {
    font-size: 0.75rem;
    color: #888;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 0.9rem;
    font-weight: 500;
    color: #222;
  }

  .info-sub {
    font-size: 0.78rem;
    color: #aaa;
  }

  .trip-location-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .location-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .location-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-top: 4px;
    flex-shrink: 0;

    &.dot-start {
      background: #28a745;
    }

    &.dot-end {
      background: #dc3545;
    }
  }

  .trip-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;

    th,
    td {
      padding: 8px 10px;
      text-align: left;
      border-bottom: 1px solid #f0f0f0;
    }

    th {
      font-size: 0.78rem;
      color: #888;
      font-weight: 500;
    }

    td.info-label {
      color: #888;
    }
  }
}
</style>
